import { useState } from 'react'
import { ManualRedispatchResult } from '@/console/api/types'
import { toPanelError } from '@/console/api/errors'
import { requestManualRedispatch, useRedispatchResults } from '@/console/data/redispatch'
import { PanelState } from '@/console/state/panelState'
import { MAX_REDISPATCH_IDS, REDISPATCH_BATCH_SIZE, RedispatchOutcome, chunkIds, classifyRedispatchResult } from './redispatch'

export type RedispatchSource = 'manual' | 'search'

/**
 * 재발사 후 조회 결과에서 체크를 푸는 판정(4차 리뷰). **실제로 나간 것만** 푼다.
 *  - `submitted` — 옵티마이저로 제출됐다.
 *  - `dedup` — 서버가 중복으로 흡수했다. 이미 같은 주문이 처리 중이라 다시 쏠 대상이 아니다.
 *
 * `accepted-not-submitted`(접수 · 미제출)와 `failed` 는 남긴다 — 그 주문은 옵티마이저로 가지 않았고
 * 담당자가 그대로 다시 보내야 하는데, 체크가 풀리면 결과 목록을 보고 id 를 손으로 다시 넣어야 한다.
 */
const SELECTION_CLEARING_OUTCOMES: RedispatchOutcome[] = ['submitted', 'dedup']

export type RedispatchController = {
  pending: { ids: number[]; source: RedispatchSource } | null
  wakeUp: boolean
  setWakeUp: (value: boolean) => void
  submitting: boolean
  resultsState: PanelState<ManualRedispatchResult[]>
  /**
   * PR 코멘트(LOGISPRD-13034, H2) — 가장 최근 제출의 응답에서 **실제로 나간** standardOrderId 목록.
   * 아직 한 번도 성공하지 않았으면 `null`.
   *
   * 두 가지를 일부러 뺀다.
   *  - 요청이 통째로 실패하면(.catch) 이 값을 건드리지 않는다 — 다시 시도할 대상이라 선택을 지울
   *    이유가 없다.
   *  - 재발사되지 않은 행도 제외한다 — `failed` 는 물론 `accepted: true` 라도 옵티마이저로 가지 않은
   *    `accepted-not-submitted` 까지(`SELECTION_CLEARING_OUTCOMES` 참고). 체크가 남아야 결과를 보고
   *    그대로 다시 보낼 수 있다.
   *
   * `OrderSearchPanel` 은 이 값이 바뀔 때만 그 id 들을 체크에서 뺀다(화면에서 사라진 행이 아니라
   * "방금 나간 id" 만).
   */
  lastSubmittedIds: number[] | null
  /** LOGISPRD-13188 — 제출 중인 묶음 번호(1부터) · 전체 묶음 수 · 응답받은 건수 · 전체 건수. 제출 중이 아니면 `null`. */
  progress: { batch: number; batches: number; done: number; total: number } | null
  /**
   * 묶음이 실패해 멈췄을 때 실패한 묶음 번호와 보내지 못한 건수(실패한 묶음 포함). 첫 묶음 실패는
   * `resultsState` 의 error 로 충분해 `null` 이다. 앞 묶음 결과가 있으면 `resultsState` 가 ready 라 에러 문구도 여기 싣는다.
   */
  halted: { batch: number; unsent: number; message: string } | null
  ask: (ids: number[], source: RedispatchSource) => void
  cancel: () => void
  confirm: () => void
}

/**
 * LOGISPRD-13034 — `RedispatchPanel` 안에 있던 제출 상태를 끌어올렸다. F-OPT 조회 패널에서 고른 행도
 * 같은 확인 다이얼로그 · 같은 잠금 · 같은 결과 섹션을 타야 해서다(재발사 경로를 둘로 만들지 않는다).
 *
 * 12506 Task 14 의 제약은 그대로다 — 응답이 올 때까지 `submitting` 이 잠그고, 실패해도 스스로 다시
 * 부르지 않는다. 다이얼로그를 여는 시점(`ask`)에 id 를 고정한다 — 확인을 누르는 사이 조회 결과가
 * 바뀌어도 사용자가 다이얼로그에서 본 id 만 나간다.
 */
export const useManualRedispatch = (): RedispatchController => {
  const baseResultsState = useRedispatchResults()
  const [pending, setPending] = useState<RedispatchController['pending']>(null)
  const [wakeUp, setWakeUp] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submittedResults, setSubmittedResults] = useState<ManualRedispatchResult[] | null>(null)
  const [submitError, setSubmitError] = useState<{ kind: 'forbidden' | 'other'; message: string } | null>(null)
  const [lastSubmittedIds, setLastSubmittedIds] = useState<number[] | null>(null)
  const [progress, setProgress] = useState<RedispatchController['progress']>(null)
  const [halted, setHalted] = useState<RedispatchController['halted']>(null)

  // LOGISPRD-13188 — 앞 묶음의 결과가 있으면 제출 중이거나 뒤 묶음이 실패했어도 결과를 보여준다.
  // 결과가 한 건도 없을 때만 예전처럼 loading · error 로 낸다.
  const resultsState: PanelState<ManualRedispatchResult[]> =
    submittedResults && submittedResults.length > 0
      ? { status: 'ready', data: submittedResults }
      : submitting
        ? { status: 'loading' }
        : submitError
          ? { status: 'error', ...submitError }
          : submittedResults
            ? { status: 'ready', data: submittedResults }
            : baseResultsState

  const ask = (ids: number[], source: RedispatchSource): void => {
    if (submitting || ids.length === 0 || ids.length > MAX_REDISPATCH_IDS) return
    setPending({ ids, source })
  }

  const confirm = (): void => {
    if (!pending || submitting) return
    const { ids } = pending
    const batches = chunkIds(ids)
    setPending(null)
    setSubmitting(true)
    setSubmitError(null)
    setSubmittedResults(null)
    setHalted(null)

    // LOGISPRD-13188 — 묶음을 하나씩 기다렸다 보낸다(병렬 금지). 실패하면 그 자리에서 멈추고
    // 뒤 묶음은 보내지 않는다 — 자동 재시도도 없다.
    // ponytail: 탭을 벗어나 언마운트돼도 이미 확인받은 뒤 묶음은 계속 나간다. 멈춰야 하면 AbortController 를 단다.
    // 성공 콜백 안에서 다음 묶음을 부른다 — 앞 묶음 응답 전에는 다음 요청이 나가지 않는다.
    const send = (index: number): Promise<void> => {
      if (index >= batches.length) return Promise.resolve()
      const done = index * REDISPATCH_BATCH_SIZE
      setProgress({ batch: index + 1, batches: batches.length, done, total: ids.length })

      return requestManualRedispatch({ standardOrderIds: batches[index], wakeUp }).then(
        (response) => {
          setSubmittedResults((prev) => [...(prev ?? []), ...response.results])
          // H2 — 요청에 실은 ids 가 아니라 응답에 담겨 온 standardOrderId 만 뺀다 — 서버가 일부만
          // 받아들였을 수 있어서다. 판정은 화면 표기와 같은 단일 출처로 한다(4차 리뷰).
          // 묶음마다 새 배열로 바꿔 넣는다 — `OrderSearchPanel` 이 값이 바뀔 때마다 그 id 만 체크를 풀어 누적된다.
          setLastSubmittedIds(
            response.results.filter((r) => SELECTION_CLEARING_OUTCOMES.includes(classifyRedispatchResult(r))).map((r) => r.standardOrderId),
          )
          return send(index + 1)
        },
        (error) => {
          const panelError = toPanelError(error)
          setSubmitError(panelError)
          if (index > 0) setHalted({ batch: index + 1, unsent: ids.length - done, message: panelError.message })
        },
      )
    }

    send(0)
      .catch((error) => setSubmitError(toPanelError(error)))
      .finally(() => {
        setSubmitting(false)
        setProgress(null)
      })
  }

  return { pending, wakeUp, setWakeUp, submitting, resultsState, lastSubmittedIds, progress, halted, ask, cancel: () => setPending(null), confirm }
}
